import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, CheckCircle2, AlertCircle, ArrowLeft } from "lucide-react";

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { forgotPassword } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    
    if (!email) {
      setError('Bitte geben Sie Ihre E-Mail-Adresse ein');
      return;
    }

    try {
      setLoading(true);
      const result = await forgotPassword(email);
      
      if (result) {
        setSuccess(true);
      } else {
        setError('Link konnte nicht gesendet werden. Prüfen Sie die E-Mail-Adresse.');
      }
    } catch (err) {
      setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 flex flex-col justify-center items-center p-4">
        <div className="w-full max-w-md space-y-8">
          <div className="flex justify-center mb-4">
            <Logo />
          </div>

          <Card className="border-border shadow-sm">
            <CardContent className="p-6 md:p-8">
              <div className="text-center mb-6">
                <h1 className="text-2xl font-semibold text-foreground mb-2">Passwort zurücksetzen</h1>
                <p className="text-sm text-muted-foreground">
                  Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Code zum Zurücksetzen.
                </p>
              </div>

              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {success ? (
                <div className="text-center space-y-6">
                  <Alert className="bg-success/10 border-success/25 text-success-strong">
                    <CheckCircle2 className="h-4 w-4 text-success-strong" />
                    <AlertDescription>
                      Der Link wurde gesendet. Bitte prüfen Sie Ihren Posteingang für den OTP-Code.
                    </AlertDescription>
                  </Alert>
                  <Button asChild variant="outline" className="w-full">
                    <RouterLink to="/login">Zurück zur Anmeldung</RouterLink>
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">E-Mail-Adresse</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="name@firma.de"
                      value={email}
                      onChange={handleChange}
                      disabled={loading}
                      required
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#1967D2] hover:bg-[#1756B6]" 
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Link senden'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {!success && (
            <div className="text-center">
              <Button asChild variant="link" className="text-muted-foreground hover:text-foreground">
                <RouterLink to="/login" className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" /> Zurück zur Anmeldung
                </RouterLink>
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Footer */}
      <div className="py-4 flex justify-center items-center opacity-60">
        <span className="text-xs text-muted-foreground mr-2">powered by</span>
        <img src="/images/EcomTask_logo.svg" alt="EcomTask" className="h-5 opacity-60 grayscale" />
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
